/**
 * locationService.js - Servicio para manejo de geolocalización del usuario
 *
 * Funcionalidades:
 * - Guardar/obtener ubicación en localStorage
 * - Solicitar ubicación del navegador (GPS)
 * - Listado de ciudades para selección manual
 */

import { API_URL } from '../../services/api/config';

const STORAGE_KEY = 'lemmon_user_location';

/**
 * Obtener ubicación guardada en localStorage
 * @returns {Object|null} - { lat, lng, ciudad, source, timestamp }
 */
export function getStoredLocation() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const location = JSON.parse(stored);
      // Validar que tenga los campos requeridos
      if (location.lat && location.lng) {
        return location;
      }
    }
  } catch (err) {
    console.error('[locationService] Error leyendo ubicación:', err);
  }
  return null;
}

/**
 * Guardar ubicación en localStorage
 * @param {Object} location - { lat, lng, ciudad, source }
 */
export function saveLocation(location) {
  try {
    const data = {
      lat: location.lat,
      lng: location.lng,
      ciudad: location.ciudad || null,
      colonia: location.colonia || null,
      source: location.source, // 'gps' | 'manual'
      timestamp: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    console.log('[locationService] Ubicación guardada:', data);
  } catch (err) {
    console.error('[locationService] Error guardando ubicación:', err);
  }
}

/**
 * Limpiar ubicación de localStorage
 */
export function clearLocation() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('[locationService] Ubicación eliminada');
  } catch (err) {
    console.error('[locationService] Error eliminando ubicación:', err);
  }
}

/**
 * Solicitar ubicación del navegador (GPS)
 * @returns {Promise<Object>} - { lat, lng, source: 'gps' }
 */
export function requestBrowserLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocalización no soportada en este navegador'));
      return;
    }

    console.log('[locationService] Solicitando ubicación del navegador...');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          source: 'gps'
        };
        console.log('[locationService] Ubicación obtenida:', location);
        resolve(location);
      },
      (error) => {
        let message;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = 'Permiso de ubicación denegado';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Ubicación no disponible';
            break;
          case error.TIMEOUT:
            message = 'Tiempo de espera agotado';
            break;
          default:
            message = 'Error desconocido al obtener ubicación';
        }
        console.error('[locationService] Error:', message);
        reject(new Error(message));
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000 // 5 minutos cache del navegador
      }
    );
  });
}

/**
 * Ciudades disponibles para selección manual
 * Ordenadas por importancia/cercanía al mercado objetivo
 */
export const CIUDADES_DISPONIBLES = [
  { nombre: 'Xalapa, Veracruz', lat: 19.5438, lng: -96.9102 },
  { nombre: 'Veracruz, Veracruz', lat: 19.1738, lng: -96.1342 },
  { nombre: 'Boca del Río, Veracruz', lat: 19.1057, lng: -96.1080 },
  { nombre: 'Coatepec, Veracruz', lat: 19.4569, lng: -96.9613 },
  { nombre: 'Ciudad de México', lat: 19.4326, lng: -99.1332 },
  { nombre: 'Puebla, Puebla', lat: 19.0414, lng: -98.2063 },
  { nombre: 'Monterrey, Nuevo León', lat: 25.6866, lng: -100.3161 },
  { nombre: 'Guadalajara, Jalisco', lat: 20.6597, lng: -103.3496 },
];

/**
 * Obtener texto descriptivo de la ubicación
 * @param {Object} location - Ubicación guardada
 * @returns {string} - Texto para mostrar al usuario
 */
export function getLocationDisplayText(location) {
  if (!location) return null;

  if (location.ciudad && location.colonia) {
    return `${location.ciudad}, ${location.colonia}`;
  }

  if (location.ciudad) {
    return location.ciudad;
  }

  if (location.source === 'gps') {
    return 'Tu ubicación';
  }

  return 'Ubicación guardada';
}

/**
 * Reverse geocoding: obtener ciudad y colonia a partir de coordenadas
 * @param {number} lat - Latitud
 * @param {number} lng - Longitud
 * @returns {Promise<{ciudad: string|null, colonia: string|null}>}
 */
export async function reverseGeocode(lat, lng) {
  try {
    const response = await fetch(
      `${API_URL}/api/directory/reverse-geocode?lat=${lat}&lng=${lng}`
    );

    if (!response.ok) {
      return { ciudad: null, colonia: null };
    }

    const result = await response.json();
    return result.data || { ciudad: null, colonia: null };
  } catch (err) {
    console.warn('[locationService] Error en reverse geocoding:', err.message);
    return { ciudad: null, colonia: null };
  }
}

/**
 * Obtener ubicación guardada y resolver nombre si es GPS sin ciudad
 * @returns {Promise<Object|null>} - Ubicación con ciudad/colonia resueltos
 */
export async function resolveStoredLocation() {
  const stored = getStoredLocation();
  if (!stored) return null;

  // Si es GPS sin ciudad resuelta, hacer reverse geocoding
  if (stored.source === 'gps' && !stored.ciudad && stored.lat && stored.lng) {
    const geo = await reverseGeocode(stored.lat, stored.lng);
    if (geo.ciudad) {
      stored.ciudad = geo.ciudad;
      stored.colonia = geo.colonia || null;
      saveLocation(stored);
    }
  }

  return stored;
}

export default {
  getStoredLocation,
  saveLocation,
  clearLocation,
  requestBrowserLocation,
  reverseGeocode,
  resolveStoredLocation,
  getLocationDisplayText,
  CIUDADES_DISPONIBLES
};
